import React, { useEffect } from 'react';
import requiredIf from 'react-required-if';
import PropTypes from 'prop-types';
import { getIn } from 'formik';
import { get, isEmpty, omitBy } from 'lodash';

import { localeDetails } from '~/components/CmsMain/localeGlobals';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import YesNoNaUnknownQuestionFormik from '~/components/core/Formik/YesNoNaUnknownQuestionFormik';
import WrapFieldWithAsterisk from '~/components/core/Molecules/WrapFieldWithAsterisk/WrapFieldWithAsterisk';
import { useCms } from '~/components/hooks/useCms';
import useIsConfigurationFieldSupportedByCategories from '~/components/hooks/useIsConfigurationFieldSupportedByCategories';
import useSafeFormikContext from '~/components/hooks/useSafeFormikContext';
import HoverActionField from '~/components/HoverActionField';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';

import { COUNTRY_TO_STATE_MAP } from '../../Types';
import { getAllSearchableContactRoles } from '../communications/ContactUtils';
import WithConfirm, { ConfirmModal } from '../ConfirmModal';
import ContactTextFieldFormik, { ContactShowOnlyTextField } from '../ContactTextFieldFormik';
import useCurrencyFormatter from '../CurrencyFormatterContext';
import NcicCodeFragment from '../Fnol/NewFnolUI/InvolvedParties/AutoParties/NcicCodeFragment';
import { LossLocationShowOnly, LossLocationTextFieldFormik } from '../GlobalLossLocation';
import { useLob } from '../hooks/useLob';
import HoverChangeField, { SelectHoverChangeField } from '../HoverChangeField';
import useOrganization from '../OrganizationContext';
import TextFieldFormik, {
  DatePickerTextFieldFormik,
  MonetaryValueTextFieldFormik,
  MultiSelectTextFieldFormik,
  NumericTextFieldFormik,
  ShowOnlyTextField,
  TimePickerTextFieldFormik,
  YearPickerSelectTextFieldFormik,
} from '../TextFieldFormik';

import { configuredFieldValueText, getRegionStateOptions } from './configuredFieldValueText';
import useCustomFields, { CustomFieldsContextProvider, getAdditionalDataValidations } from './CustomFieldsContext';

import { useStyles } from '../../assets/styles';

const METADATA_FIELDS = [
  'lob_specific',
  'inherit',
  'type',
  'desc',
  'active',
  'incident_sub_type_categories',
  'db_field',
];
const CONFIGURABLE_FIELDS_ID_PREFIX = 'configured_fields_values';
export const FULL_ROW_TYPES = ['yes_no_na_unknown', 'multiline_string'];
const WRAPPED_TYPES = ['location'];

// Inline configured-field labels can be long questions; `clampLabelToSingleLine` on the core field
// components keeps them on one line (ellipsis + hover tooltip) so they don't wrap through the input
// underline and misalign the field. Wired into every inline editable renderer that routes through
// the core TextField / DatePickerField (string, select, year, number, monetary, date, time).

const getInitialSectionConfig = (incidentConfiguration, configPath, defaultConfig, isFieldSupportedByCategories) => {
  const config = { ...get(incidentConfiguration, configPath, defaultConfig) }; // spread config to not touch the original config

  const isActive = (value) => value?.active === true;
  const isMandatory = (value) => value?.mandatory === true;
  const matchGeoSpecific = (value) => !shouldNotDisplayRegionSpecificField(value);
  const matchIncidentSubtype = (value) => isFieldSupportedByCategories && isFieldSupportedByCategories(value);
  const sectionShouldBeMandatory = (value) =>
    isActive(value) && isMandatory(value) && matchGeoSpecific(value) && matchIncidentSubtype(value);

  if (Object.values(config).some((value) => sectionShouldBeMandatory(value))) {
    config.mandatory = true;
  }

  return config;
};

const preparePreDefinedFields = (fields, additionalFieldOmitFn) => {
  if (isEmpty(fields)) {
    return {};
  }
  const preDefinedFields = omitBy(
    fields,
    (field) =>
      !field?.active ||
      shouldNotDisplayRegionSpecificField(field) ||
      (additionalFieldOmitFn && !additionalFieldOmitFn(field))
  );
  Object.keys(preDefinedFields).forEach((key) => {
    if (METADATA_FIELDS.includes(key)) {
      delete preDefinedFields[key];
    } else if (preDefinedFields[key]) {
      preDefinedFields[key].id = key; // in pre_defined_fields the id is not inside the field object
    }
  });

  return preDefinedFields;
};

// Configured-fields counterpart of preparePreDefinedFields: same keep-predicate, applied to the
// array form. Keeps the keep/omit polarity defined in one place for both field shapes.
const prepareConfiguredFields = (fields, additionalFieldOmitFn) =>
  (fields || []).filter((field) => (additionalFieldOmitFn ? additionalFieldOmitFn(field) : true));

const getConfiguredFieldGridXs = (field, newLayoutEnabled) => {
  if (newLayoutEnabled) {
    return 3;
  }
  return FULL_ROW_TYPES.includes(field?.type) ? 12 : 6;
};

const getInitialValuesWithIncidentTypes = (values, path) => ({
  incident_type: values?.incident_type,
  incident_sub_type: values?.incident_sub_type,
  ...get(values, path),
});

export const getBaseConfiguredFieldsValidations = (configuredFields) => {
  const validations = {};
  configuredFields.forEach((field) => {
    validations[field.id] = field;
  });
  return getAdditionalDataValidations(validations);
};

const getConfiguredFieldsValidations = (incidentConfiguration, section = 'incident_details', additionalFieldOmitFn) => {
  const configuredFields = incidentConfiguration?.incident_details?.configured_fields.filter((field) =>
    additionalFieldOmitFn ? additionalFieldOmitFn(field) : true
  );

  if (isEmpty(configuredFields)) {
    return {};
  }

  const fields = {};
  // 'incident_details' owns both explicitly-tagged and untagged fields; every other section
  // (supplementary_info and the config-driven custom sections) matches its id exactly.
  const fieldsArray =
    section === 'incident_details'
      ? configuredFields?.filter((field) => field?.section === 'incident_details' || !field?.section) || []
      : configuredFields?.filter((field) => field?.section === section) || [];

  fieldsArray?.forEach((field) => {
    fields[field?.id] = field;
  });

  return getAdditionalDataValidations(fields);
};

const shouldNotDisplayRegionSpecificField = (field) => {
  const region = localeDetails.locale.region;

  // us_state_select: hide entirely for regions with no state map (avoid empty grid
  // slots from the null-rendering control); for regions that do have one, ignore
  // any stored geo_specific so legacy US-tagged configs still render on AU.
  if (field?.type === 'us_state_select') {
    return !COUNTRY_TO_STATE_MAP[region];
  }

  return field?.geo_specific && field.geo_specific !== region;
};

const isWrappedField = (field) => WRAPPED_TYPES.includes(field?.type);

const isPreDefinedFieldHidden = (field) => !field?.active || shouldNotDisplayRegionSpecificField(field);

const PreDefinedFieldIsolated = ({
  id,
  fields,
  value,
  onUpdate,
  readOnly = false,
  inline = false,
  rows = undefined,
  desc = undefined,
  idPrefix = undefined,
  staticEditIcon = undefined,
  ...rest
}) => {
  const formikContext = useSafeFormikContext();
  const field = get(fields, id);

  if (isPreDefinedFieldHidden(field)) {
    return null;
  }

  field.id = id; // in pre_defined_fields the id is not inside the field object
  field.desc = desc || field.desc;
  field.disableFuture = rest?.disableFuture || false; // disableFuture also used inside the validations
  const full_id = idPrefix ? `${idPrefix}.${id}` : field.id;
  const fieldValue = value || (formikContext ? getIn(formikContext.values, full_id) : undefined);

  return (
    <CustomFieldsContextProvider customFieldsConfigurations={[field]}>
      <CustomField
        field={field}
        idPrefix={idPrefix}
        value={fieldValue}
        onUpdate={onUpdate}
        readOnly={readOnly}
        inline={inline}
        rows={rows}
        staticEditIcon={staticEditIcon}
        {...rest}
      />
    </CustomFieldsContextProvider>
  );
};

const PreDefinedField = ({ id, fields, saveGrid = false, newRowAfter = false, gridXs = undefined, ...rest }) => {
  const field = get(fields, id);
  const xs = gridXs || getConfiguredFieldGridXs(field);

  if (isPreDefinedFieldHidden(field)) {
    return saveGrid ? <Grid item xs={xs} /> : <></>;
  }

  return (
    <>
      <Grid item xs={xs}>
        <PreDefinedFieldIsolated id={id} fields={fields} {...rest} />
      </Grid>
      {newRowAfter && <Grid item xs={12 - xs} />}
    </>
  );
};

PreDefinedFieldIsolated.propTypes = {
  id: PropTypes.string.isRequired,
  fields: PropTypes.object.isRequired,
  value: PropTypes.any,
  onUpdate: requiredIf(PropTypes.func, (props) => !props.inline && !props.readOnly),
  readOnly: PropTypes.bool,
  inline: PropTypes.bool,
  rows: PropTypes.number,
  desc: PropTypes.string,
  idPrefix: PropTypes.string,
  staticEditIcon: PropTypes.bool,
  disableFuture: PropTypes.bool,
};

PreDefinedField.propTypes = {
  ...PreDefinedFieldIsolated.propTypes,
  saveGrid: PropTypes.bool,
  newRowAfter: PropTypes.bool,
  gridXs: PropTypes.number,
};

const ConfiguredFields = ({
  values,
  customFields,
  onFieldUpdate,
  readOnly = false,
  inline = false,
  idPrefix = CONFIGURABLE_FIELDS_ID_PREFIX,
  newLayoutEnabled = false,
  wrapperProps,
}) => {
  const FieldsWrapper = newLayoutEnabled ? React.Fragment : Grid;
  const fieldsWrapperProps = newLayoutEnabled ? {} : wrapperProps ? wrapperProps : { container: true, spacing: 1 };
  return (
    <CustomFieldsContextProvider customFieldsConfigurations={customFields}>
      <FieldsWrapper {...fieldsWrapperProps}>
        {customFields
          ?.filter((field) => !(field?.active === false))
          .map((field) => (
            <Grid item xs={getConfiguredFieldGridXs(field, newLayoutEnabled)} key={field.id}>
              <CustomField
                field={field}
                value={get(values, idPrefix ? `${idPrefix}.${field.id}` : field.id)}
                onUpdate={onFieldUpdate}
                readOnly={readOnly}
                inline={inline}
                idPrefix={idPrefix}
              />
            </Grid>
          ))}
      </FieldsWrapper>
    </CustomFieldsContextProvider>
  );
};

ConfiguredFields.propTypes = {
  values: PropTypes.object.isRequired,
  customFields: PropTypes.array.isRequired,
  onFieldUpdate: requiredIf(PropTypes.func, (props) => !props.inline),
  readOnly: PropTypes.bool,
  inline: PropTypes.bool,
  idPrefix: PropTypes.string,
  newLayoutEnabled: PropTypes.bool,
  wrapperProps: PropTypes.object,
};

const customFieldsBasePropTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.any,
  onUpdate: requiredIf(PropTypes.func, (props) => !props.inline && !props.readOnly),
  readOnly: PropTypes.bool.isRequired,
  inline: PropTypes.bool.isRequired,
  idPrefix: PropTypes.string,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  overFlowWithTooltip: PropTypes.bool,
  staticEditIcon: PropTypes.bool,
};

const CustomField = ({
  field,
  value,
  onUpdate,
  readOnly,
  inline,
  rows = undefined,
  idPrefix = undefined,
  maxWidth,
  overFlowWithTooltip,
  staticEditIcon,
  width,
  hoverActionWidth,
  ...rest
}) => {
  const onFieldUpdate = async (updatedValues) =>
    await onUpdate(field.id, isWrappedField(field) ? updatedValues : updatedValues[field.id]);

  const { userOrganization } = useCms();
  const isAsteriskEnabled = Boolean(
    userOrganization.configuration?.fnol_configuration?.show_asterisk_on_mandatory_fields
  );

  const props = {
    field,
    value,
    readOnly,
    inline,
    onUpdate: onFieldUpdate,
    rows,
    idPrefix,
    staticEditIcon,
    ...rest,
  };

  const componentMap = {
    string: (props, { overFlowWithTooltip, maxWidth, value }) => (
      <TextCustomField
        {...props}
        value={
          overFlowWithTooltip ? <OverflowTextWithToolTip maxWidth={maxWidth}>{value}</OverflowTextWithToolTip> : value
        }
      />
    ),
    multiline_string: (props, { rows, width, hoverActionWidth }) => (
      <TextCustomField {...props} rows={rows} width={width} hoverActionWidth={hoverActionWidth} />
    ),
    select: (props) => <SelectCustomField {...props} />,
    us_state_select: (props) => {
      // Country-aware: render the org region's state list (US, AU). Hide for regions
      // without a state list. The name `us_state_select` is kept for backward compat
      // and tracked as tech debt for a future rename.
      const stateOptions = getRegionStateOptions();
      if (!stateOptions) {
        return null;
      }
      return <SelectCustomField overrideOptions={stateOptions} {...props} />;
    },
    multiselect: (props) => <SelectCustomField isMultiSelect {...props} />,
    date: (props) => <DateCustomField {...props} />,
    year: (props) => <YearCustomField {...props} />,
    time: (props) => <TimeCustomField {...props} />,
    number: (props) => <NumericCustomField {...props} />,
    boolean: (props) => <BoolCustomField additionalChildrenProps={{ width: '300px' }} {...props} />,
    contact: (props, { maxWidth }) => <ContactCustomField {...props} maxWidth={maxWidth} />,
    yes_no_na_unknown: (props) => <YesNoNaUnknownQuestionCustomField {...props} />,
    monetary: (props) => <MonetaryValueCustomField {...props} />,
    location: (props) => <LocationCustomField {...props} />,
    make_ncic_code: (props) => <NcicCodeFragment {...props} />,
  };

  const componentFactory = componentMap[field.type];
  const component = componentFactory ? (
    componentFactory(props, {
      overFlowWithTooltip,
      maxWidth,
      value,
      rows,
      width,
      hoverActionWidth,
    })
  ) : (
    <></>
  );

  const showAsterisk = isAsteriskEnabled && field.mandatory && !readOnly && inline;
  const asteriskClassName = field.type === 'yes_no_na_unknown' ? 'top-1/2' : '';

  return (
    <WrapFieldWithAsterisk showAsterisk={showAsterisk} asteriskClassName={asteriskClassName}>
      {component}
    </WrapFieldWithAsterisk>
  );
};

CustomField.propTypes = {
  ...customFieldsBasePropTypes,
  rows: PropTypes.number,
  staticEditIcon: PropTypes.bool,
};

const DEFAULT_MULTILINE_WIDTH = '450px';

const TextCustomField = ({
  field,
  value,
  onUpdate,
  readOnly,
  inline,
  rows = undefined,
  idPrefix = undefined,
  width = undefined,
  hoverActionWidth = undefined,
  staticEditIcon = undefined,
  wrapperClassName = '',
  ...rest
}) => {
  const { classes } = useStyles();
  const { additionalDataValidations } = useCustomFields();
  const id = idPrefix ? `${idPrefix}.${field.id}` : field.id;

  if (inline) {
    return (
      <TextFieldFormik
        id={id}
        label={field.desc}
        className={classes.textField}
        fullWidth
        showOnly={readOnly}
        multiline={!!rows}
        rows={rows}
        clampLabelToSingleLine
        {...rest}
      />
    );
  }

  const readOnlyField = (
    <ShowOnlyTextField
      classes={classes}
      showOnlyValueComponent={value}
      label={field.desc}
      width={width}
      wrapperClassName={wrapperClassName}
      {...rest}
    />
  );

  if (readOnly) {
    return readOnlyField;
  }

  return (
    <HoverChangeField
      name={field.id}
      value={value ?? ''}
      label={field.desc}
      onUpdate={onUpdate}
      fullWidth
      multiline
      showOnly
      rows={rows ? rows : 5}
      validationSchema={additionalDataValidations[field.id]}
      width={hoverActionWidth || width || DEFAULT_MULTILINE_WIDTH}
      permanent={staticEditIcon}
      {...rest}
    >
      {readOnlyField}
    </HoverChangeField>
  );
};

TextCustomField.propTypes = {
  ...customFieldsBasePropTypes,
  rows: PropTypes.number,
  width: PropTypes.string,
  hoverActionWidth: PropTypes.string,
  wrapperClassName: PropTypes.string,
};

const SelectCustomField = ({
  field,
  value,
  onUpdate,
  isMultiSelect = false,
  readOnly,
  inline,
  idPrefix = undefined,
  overrideOptions = undefined,
  staticEditIcon = undefined,
  ...rest
}) => {
  const { classes } = useStyles();
  const { additionalDataValidations } = useCustomFields();
  const { isOptionSupportedByCategories } = useIsConfigurationFieldSupportedByCategories();
  const id = idPrefix ? `${idPrefix}.${field.id}` : field.id;
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const formikContext = useSafeFormikContext();

  field.options = overrideOptions || field.options || [];

  const selectIdsList = isMultiSelect ? value : [value];

  const disabledIdsSelected = field.options
    .filter((opt) => opt.active === false && selectIdsList?.includes(opt.id))
    .map((opt) => opt.id);

  // Filter options by active status and category support
  const optionsEnabledOrSelected = field.options.filter((opt) => {
    const isActive = opt.active === undefined || opt.active === true || disabledIdsSelected.includes(opt.id);
    if (!isActive) {
      return false;
    }
    // Filter by category support - ensure selected but disabled options remain visible
    const isSupported = disabledIdsSelected.includes(opt.id) || isOptionSupportedByCategories(field, opt);
    return isSupported;
  });
  const displayValueFunc = (key) => field.options.find((o) => o.id === key)?.desc;
  const valueInline = selectIdsList?.map((key) => displayValueFunc(key)).join(', ');
  const disabledSelectedOptions = disabledIdsSelected.map((id) => displayValueFunc(id)).join(', ');
  const disabledOptionsConfirmTitle = `Editing ${field.desc}?`;
  const disabledOptionsConfirmContentText = `To edit this field, please confirm the removal of the following disabled options: ${disabledSelectedOptions}`;

  useEffect(() => {
    if (!isConfirmOpen && isMultiSelect && !isEmpty(disabledIdsSelected) && inline) {
      setIsConfirmOpen(true);
    }
  }, [disabledIdsSelected, inline, isConfirmOpen, isMultiSelect]);

  if (inline) {
    if (isMultiSelect) {
      if (!isEmpty(disabledIdsSelected) && isConfirmOpen) {
        return (
          <WithConfirm
            title={disabledOptionsConfirmTitle}
            contentText={disabledOptionsConfirmContentText}
            onClose={() => setIsConfirmOpen(false)}
            primaryButtonName="OK, Remove"
            shouldCloseOnPrimary
            centerDialog
          >
            <TextFieldFormik
              id={id}
              label={field.desc}
              className={classes.textField}
              value={valueInline}
              fullWidth
              staticEditIcon={staticEditIcon}
              clampLabelToSingleLine
              onClick={() => {
                setIsConfirmOpen(false);
                formikContext?.setFieldValue(
                  id,
                  selectIdsList?.filter((id) => !disabledIdsSelected.includes(id))
                );
              }}
            />
          </WithConfirm>
        );
      }

      return (
        <MultiSelectTextFieldFormik
          id={id}
          label={field.desc}
          className={classes.textField}
          options={optionsEnabledOrSelected.map((o) => o.id)}
          // `field.sortSelected` (optional) is a comparator that orders the selected-value summary; without
          // it the summary stays in insert (selection) order, which is the default for every configured field.
          renderValue={(selected) =>
            (field.sortSelected ? [...selected].sort(field.sortSelected) : selected)
              .map((key) => displayValueFunc(key))
              .join(', ')
          }
          renderOption={displayValueFunc}
          fullWidth
          showOnly={readOnly}
          clampLabelToSingleLine
          {...rest}
        />
      );
    } else {
      if (readOnly) {
        return (
          <ShowOnlyTextField
            classes={classes}
            showOnlyValueComponent={displayValueFunc(value)}
            label={field.desc}
            staticEditIcon={staticEditIcon}
          />
        );
      }
      return (
        <TextFieldFormik
          id={id}
          label={field.desc}
          className={classes.textField}
          fullWidth
          select
          showOnly={readOnly}
          staticEditIcon={staticEditIcon}
          clearable={!readOnly}
          clampLabelToSingleLine
          {...rest}
        >
          {optionsEnabledOrSelected.map((option) => (
            <MenuItem key={option.id} value={option.id} disabled={disabledIdsSelected.includes(option.id)}>
              {option.desc}
            </MenuItem>
          ))}
        </TextFieldFormik>
      );
    }
  }

  const selectProps = {
    value: value || (isMultiSelect ? [] : null),
    label: field.desc,
    fieldId: field.id,
    keys: optionsEnabledOrSelected.map((o) => o.id),
    keysDisabled: disabledIdsSelected,
    onUpdate,
    overrideOnEdit: true,
    isMultiSelect,
    validationSchema: additionalDataValidations[field.id],
    idPrefix: PropTypes.string,
    clearable: true,
  };

  const readOnlyProps = {
    classes,
    label: field.desc,
  };

  selectProps.displayValueFunc = (id) => optionsEnabledOrSelected.find((o) => o.id === id)?.desc || '';
  readOnlyProps.showOnlyValueComponent = configuredFieldValueText(isMultiSelect ? 'multiselect' : 'select', value, {
    options: optionsEnabledOrSelected,
  });

  const readOnlyField = <ShowOnlyTextField {...readOnlyProps} />;

  if (readOnly) {
    return readOnlyField;
  }

  if (isMultiSelect && !isEmpty(disabledIdsSelected)) {
    return (
      <>
        <HoverActionField permanent={staticEditIcon} onAction={() => setIsConfirmOpen(true)} {...rest}>
          {valueInline}
        </HoverActionField>
        <ConfirmModal
          isOpen={isConfirmOpen}
          onClose={() => setIsConfirmOpen(false)}
          title={disabledOptionsConfirmTitle}
          contentText={disabledOptionsConfirmContentText}
          primaryButtonName="OK, Remove"
          onPrimaryBtnClick={async () => {
            await onUpdate({ [field.id]: selectIdsList.filter((id) => !disabledIdsSelected.includes(id)) });
            setIsConfirmOpen(false);
          }}
        />
      </>
    );
  }

  return <SelectHoverChangeField {...selectProps}>{readOnlyField}</SelectHoverChangeField>;
};

SelectCustomField.propTypes = {
  ...customFieldsBasePropTypes,
  isMultiSelect: PropTypes.bool,
};

const DateCustomField = ({
  field,
  value,
  onUpdate,
  readOnly,
  inline,
  idPrefix = undefined,
  disableFuture = undefined,
  withoutLabelOnReadOnly = false,
  staticEditIcon = undefined,
  ...rest
}) => {
  const { classes } = useStyles();
  const { additionalDataValidations } = useCustomFields();
  const id = idPrefix ? `${idPrefix}.${field.id}` : field.id;

  // eslint-disable-next-line no-unused-vars
  const { rows, ...restWithoutRows } = rest;

  if (inline && !readOnly) {
    return (
      <DatePickerTextFieldFormik
        id={id}
        label={field.desc}
        className={classes.textField}
        fullWidth
        disableFuture={disableFuture}
        staticEditIcon={staticEditIcon}
        clampLabelToSingleLine
        {...restWithoutRows}
      />
    );
  }

  return (
    <SpecialCustomField
      specialType="date"
      field={field}
      value={value}
      onUpdate={onUpdate}
      validationSchema={additionalDataValidations[field.id]}
      readOnly={readOnly}
      inline={inline}
      disableFuture={disableFuture}
      withoutLabelOnReadOnly={withoutLabelOnReadOnly}
      staticEditIcon={staticEditIcon}
    />
  );
};

DateCustomField.propTypes = {
  ...customFieldsBasePropTypes,
  disableFuture: PropTypes.bool,
  withoutLabelOnReadOnly: PropTypes.bool,
};

const TimeCustomField = ({
  field,
  value,
  onUpdate,
  readOnly,
  inline,
  idPrefix = undefined,
  staticEditIcon = undefined,
}) => {
  const { classes } = useStyles();
  const { additionalDataValidations } = useCustomFields();
  const id = idPrefix ? `${idPrefix}.${field.id}` : field.id;

  if (inline && !readOnly) {
    return (
      <TimePickerTextFieldFormik
        id={id}
        label={field.desc}
        className={classes.textField}
        fullWidth
        clampLabelToSingleLine
      />
    );
  }

  return (
    <SpecialCustomField
      specialType="time"
      field={field}
      value={value}
      onUpdate={onUpdate}
      validationSchema={additionalDataValidations[field.id]}
      readOnly={readOnly}
      inline={inline}
      staticEditIcon={staticEditIcon}
    />
  );
};

TimeCustomField.propTypes = {
  ...customFieldsBasePropTypes,
};

const YearCustomField = ({
  field,
  value,
  onUpdate,
  readOnly,
  inline,
  idPrefix = undefined,
  staticEditIcon = undefined,
  ...rest
}) => {
  const { classes } = useStyles();
  const { additionalDataValidations } = useCustomFields();
  const id = idPrefix ? `${idPrefix}.${field.id}` : field.id;

  if (inline && !readOnly) {
    return (
      <YearPickerSelectTextFieldFormik
        id={id}
        label={field.desc}
        className={classes.textField}
        fullWidth
        clampLabelToSingleLine
        {...rest}
      />
    );
  }

  return (
    <SpecialCustomField
      specialType="year"
      field={field}
      value={value}
      onUpdate={onUpdate}
      validationSchema={additionalDataValidations[field.id]}
      readOnly={readOnly}
      inline={inline}
      staticEditIcon={staticEditIcon}
    />
  );
};

YearCustomField.propTypes = {
  ...customFieldsBasePropTypes,
};

const NumericCustomField = ({
  field,
  value,
  onUpdate,
  readOnly,
  inline,
  idPrefix = undefined,
  staticEditIcon = undefined,
  ...rest
}) => {
  const { classes } = useStyles();
  const { additionalDataValidations } = useCustomFields();
  const id = idPrefix ? `${idPrefix}.${field.id}` : field.id;

  if (inline && !readOnly) {
    return (
      <NumericTextFieldFormik
        id={id}
        label={field.desc}
        className={classes.textField}
        fullWidth
        clampLabelToSingleLine
        {...rest}
      />
    );
  }

  return (
    <SpecialCustomField
      specialType="numeric"
      field={field}
      value={value}
      onUpdate={onUpdate}
      validationSchema={additionalDataValidations[field.id]}
      readOnly={readOnly}
      inline={inline}
      staticEditIcon={staticEditIcon}
    />
  );
};

NumericCustomField.propTypes = {
  ...customFieldsBasePropTypes,
};

const ContactCustomField = ({
  field,
  value,
  contactDisplayName = '',
  onUpdate,
  readOnly,
  inline,
  idPrefix = undefined,
  maxWidth = '100%', // Override default 150px cap
  staticEditIcon = undefined,
  hideReadOnlyTitle = false,
  ...rest
}) => {
  const { classes } = useStyles();
  const { additionalDataValidations } = useCustomFields();
  const { organizationContactRolesDict } = useOrganization();
  const acceptedRoles = field.accepted_roles || getAllSearchableContactRoles(organizationContactRolesDict);
  const formik = useSafeFormikContext();

  // we assume each contact field.id is of the form 'X_id' while all FE components expect to receive X and append the '_id' automatically
  const contactObjFieldId = field.id.slice(0, -1 * '_id'.length);
  const id = idPrefix ? `${idPrefix}.${contactObjFieldId}` : contactObjFieldId;

  if (inline && !readOnly) {
    const fullNameId = id?.replace(/contact$/, 'contact_full_name');
    let contactFullName = get(formik?.values, fullNameId);

    return (
      <ContactTextFieldFormik
        id={id}
        label={field.desc}
        className={classes.textField}
        acceptedRoles={acceptedRoles}
        fullWidth
        fixedSearchResults
        contactDisplayName={contactFullName}
        staticEditIcon={staticEditIcon}
        {...rest}
      />
    );
  }

  const readOnlyField = (
    <ContactShowOnlyTextField
      label={hideReadOnlyTitle ? null : field.desc}
      contactId={value ?? ''}
      contactDisplayName={contactDisplayName}
      key={value ?? ''} // otherwise - if we update, it might not get it
      maxWidth={maxWidth}
      staticEditIcon={staticEditIcon}
    />
  );

  if (readOnly) {
    return readOnlyField;
  }

  return (
    <HoverChangeField
      name={field.id}
      customFieldIdName={contactObjFieldId}
      value={value ?? ''}
      label={field.desc}
      specialFieldType="contact"
      specialFieldAdditionalProps={{ acceptedRoles }}
      onUpdate={(values) => {
        const fieldVal = values[field.id];
        return onUpdate({ [field.id]: fieldVal === '' ? null : fieldVal });
      }}
      validationSchema={additionalDataValidations[field.id]}
      permanent={staticEditIcon}
    >
      {readOnlyField}
    </HoverChangeField>
  );
};

ContactCustomField.propTypes = {
  ...customFieldsBasePropTypes,
  contactDisplayName: PropTypes.string,
  hideReadOnlyTitle: PropTypes.bool,
};

const YesNoNaUnknownQuestionCustomField = ({
  field,
  value,
  onUpdate,
  readOnly,
  inline,
  idPrefix = undefined,
  staticEditIcon = undefined,
}) => {
  const { additionalDataValidations } = useCustomFields();
  const id = idPrefix ? `${idPrefix}.${field.id}` : field.id;
  if (inline && !readOnly) {
    return (
      <YesNoNaUnknownQuestionFormik
        id={id}
        questionText={field.desc}
        disable_na={field.disable_na}
        disable_unknown={field.disable_unknown}
        clearable
      />
    );
  }

  return (
    <SpecialCustomField
      specialType="yes_no_na_unknown"
      field={field}
      value={value}
      onUpdate={onUpdate}
      validationSchema={additionalDataValidations[field.id]}
      readOnly={readOnly}
      inline={inline}
      staticEditIcon={staticEditIcon}
    />
  );
};

YesNoNaUnknownQuestionCustomField.propTypes = {
  ...customFieldsBasePropTypes,
};

const MonetaryValueCustomField = ({
  field,
  value,
  onUpdate,
  readOnly,
  inline,
  idPrefix = undefined,
  staticEditIcon = undefined,
  ...rest
}) => {
  const { classes } = useStyles();
  const { additionalDataValidations } = useCustomFields();
  const id = idPrefix ? `${idPrefix}.${field.id}` : field.id;

  if (inline && !readOnly) {
    return (
      <MonetaryValueTextFieldFormik
        id={id}
        label={field.desc}
        className={classes.textField}
        fullWidth
        clampLabelToSingleLine
        {...rest}
      />
    );
  }

  return (
    <SpecialCustomField
      specialType="monetary"
      field={field}
      value={value}
      onUpdate={onUpdate}
      validationSchema={additionalDataValidations[field.id]}
      readOnly={readOnly}
      inline={inline}
      staticEditIcon={staticEditIcon}
    />
  );
};

MonetaryValueCustomField.propTypes = {
  ...customFieldsBasePropTypes,
};

const LocationCustomField = ({
  field,
  value,
  onUpdate,
  readOnly,
  inline,
  idPrefix = undefined,
  staticEditIcon = undefined,
  ...rest
}) => {
  const { lob } = useLob();
  const id = idPrefix ? `${idPrefix}.${field.id}` : field.id;
  const withIsHighway = lob === 'auto_claim';
  const dropRequiredValidation = id !== 'loss_location' && id !== 'vehicle_location' && !field?.mandatory;

  if (inline && !readOnly) {
    return (
      <LossLocationTextFieldFormik
        id={id}
        label={field.desc}
        dropRequiredValidation={dropRequiredValidation}
        flatten
        withClearButton
        withIsHighway={withIsHighway}
        {...rest}
      />
    );
  }

  return (
    <LossLocationShowOnly
      location={value || {}}
      label={field.desc}
      onUpdate={onUpdate}
      withIsHighway={withIsHighway}
      dropRequiredValidation={dropRequiredValidation}
      staticEditIcon={staticEditIcon}
      {...rest}
    />
  );
};

LocationCustomField.propTypes = {
  ...customFieldsBasePropTypes,
};

const SpecialCustomField = ({
  field,
  value,
  onUpdate,
  specialType,
  validationSchema,
  readOnly,
  disableFuture = undefined,
  withoutLabelOnReadOnly = false,
  staticEditIcon = undefined,
}) => {
  const { classes } = useStyles();
  const { currencyFormatter } = useCurrencyFormatter();

  const formattedValue = configuredFieldValueText(specialType === 'numeric' ? 'number' : specialType, value, {
    formatCurrency: currencyFormatter.format,
  });

  const readOnlyField = (
    <ShowOnlyTextField
      classes={classes}
      showOnlyValueComponent={formattedValue}
      label={!withoutLabelOnReadOnly ? field.desc : undefined}
      staticEditIcon={staticEditIcon}
    />
  );

  if (readOnly) {
    return readOnlyField;
  }

  const specialFieldAdditionalProps =
    specialType === 'date' || specialType === 'time'
      ? {
          disableFuture,
          clearable: true,
          nullIfEmpty: true,
        }
      : {};

  return (
    <HoverChangeField
      name={field.id}
      value={value ?? ''}
      label={field.desc}
      specialFieldType={specialType}
      specialFieldProps={field}
      onUpdate={onUpdate}
      validationSchema={validationSchema}
      specialFieldAdditionalProps={specialFieldAdditionalProps}
      permanent={staticEditIcon}
      clearable={specialType === 'yes_no_na_unknown'}
      clearValue={specialType === 'yes_no_na_unknown' ? null : undefined}
    >
      {readOnlyField}
    </HoverChangeField>
  );
};

SpecialCustomField.propTypes = {
  ...customFieldsBasePropTypes,
  specialType: PropTypes.oneOf(['date', 'time', 'numeric', 'year', 'yes_no_na_unknown', 'monetary']).isRequired,
  validationSchema: PropTypes.object,
};

const BOOL_OPTIONS = [
  {
    label: 'Yes',
    optionValue: true,
  },
  {
    label: 'No',
    optionValue: false,
  },
];

const BoolCustomField = ({
  field,
  value,
  onUpdate,
  additionalChildrenProps,
  readOnly,
  inline,
  idPrefix = undefined,
  staticEditIcon = undefined,
}) => {
  const { classes } = useStyles();
  const { additionalDataValidations } = useCustomFields();
  const id = idPrefix ? `${idPrefix}.${field.id}` : field.id;

  if (inline && !readOnly) {
    return (
      <YesNoNaUnknownQuestionFormik
        id={id}
        questionText={field.desc}
        disabled={readOnly}
        options={BOOL_OPTIONS}
        clearable
      />
    );
  }

  const fieldText = configuredFieldValueText('boolean', value);

  const readOnlyField = (
    <ShowOnlyTextField
      classes={classes}
      showOnlyValueComponent={fieldText}
      label={field.desc}
      staticEditIcon={staticEditIcon}
    />
  );

  if (readOnly) {
    return readOnlyField;
  }

  return (
    <HoverChangeField
      name={field.id}
      value={value ?? ''}
      label={field.desc}
      specialFieldType="yes_no_na_unknown"
      specialFieldProps={{ field, options: BOOL_OPTIONS }}
      onUpdate={onUpdate}
      validationSchema={additionalDataValidations[field.id]}
      permanent={staticEditIcon}
      clearable
      {...additionalChildrenProps}
    >
      {readOnlyField}
    </HoverChangeField>
  );
};

BoolCustomField.propTypes = {
  ...customFieldsBasePropTypes,
  additionalChildrenProps: PropTypes.object,
};

function getConfiguredFieldsEmptyFormikInitialValues(
  fieldsConfig,
  section = 'incident_details',
  additionalFieldOmitFn
) {
  const initialValues = {};
  fieldsConfig
    .filter((field) => (additionalFieldOmitFn ? additionalFieldOmitFn(field) : true))
    .forEach((field) => {
      if (field?.section === section || (!field?.section && section === 'incident_details')) {
        if (field.type === 'multiselect') {
          initialValues[field.id] = [];
        } else {
          initialValues[field.id] = '';
        }
      }
    });
  return initialValues;
}

const getPredefinedFieldsEmptyFormikInitialValues = (fieldsConfig) => {
  const initialValues = {};
  Object.values(fieldsConfig).forEach((field) => {
    if (field.type === 'pre_defined_fields') {
      return;
    } else if (field.type === 'multiselect') {
      initialValues[field.id] = [];
    } else {
      initialValues[field.id] = '';
    }
  });
  return initialValues;
};

function cleanConfiguredFieldsFormikValuesBeforeSend(fieldsConfig, values, options = {}) {
  const {
    additionalFieldOmitFn,
    shouldSendNullForEmpty, // useful for PATCH operations
    // Group values are nested objects and do not use scalar or contact-field conversions.
    fieldGroupKeys = [],
  } = options;
  const valuesClean = {};
  fieldsConfig
    .filter((field) => (additionalFieldOmitFn ? additionalFieldOmitFn(field) : true))
    .forEach((field) => {
      const k = field.id;
      if (values[k] === '' || values[k] === null) {
        if (shouldSendNullForEmpty) {
          valuesClean[k] = null;
        }
      } else if (values[k] !== undefined) {
        valuesClean[k] = values[k];
        if (k.endsWith('contact_id')) {
          const contactKeyWithoutId = k.slice(0, -1 * '_id'.length);
          valuesClean[contactKeyWithoutId] = values[contactKeyWithoutId];
        }
      }
    });
  fieldGroupKeys.forEach((k) => {
    if (values[k] !== undefined) {
      valuesClean[k] = values[k];
    }
  });
  return valuesClean;
}

export {
  cleanConfiguredFieldsFormikValuesBeforeSend,
  getConfiguredFieldsEmptyFormikInitialValues,
  getConfiguredFieldsValidations,
  getInitialSectionConfig,
  getInitialValuesWithIncidentTypes,
  getPredefinedFieldsEmptyFormikInitialValues,
  PreDefinedField,
  PreDefinedFieldIsolated,
  prepareConfiguredFields,
  preparePreDefinedFields,
  shouldNotDisplayRegionSpecificField,
};

export { CustomField };
export default ConfiguredFields;
